/**
 * PublicBooking — /l/{slug}/book
 *
 * The frictionless booking page hosted on EFGH for a specific lab.
 * A patient lands here from a Smart Report's "Book Now" CTA (when the lab uses EFGH
 * booking mode, or when the WhatsApp fallback triggers).
 *
 * Submits to `POST /api/labs/{lab_id}/booking-requests`. The lab is notified via
 * their booking_notify_email (Resend) and can act on it from their Lab Admin queue.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Clock, Home, Building2, ArrowRight, CheckCircle2, Tag, Package as PackageIcon, Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import LabPromoBannerCarousel from "@/components/lab/LabPromoBannerCarousel";

const formatSlot = (hhmm) => {
  const [hStr, mStr] = String(hhmm).split(":");
  const h = Number(hStr); const m = Number(mStr);
  if (!Number.isFinite(h)) return hhmm;
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
};

export default function PublicBooking() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Prefill from query string (comes from Smart Report deep-link)
  const initialTests = useMemo(() => (params.get("tests") || "").split("|").filter(Boolean), [params]);
  const [form, setForm] = useState({
    patient_name: params.get("name") || "",
    patient_phone: params.get("phone") || "",
    patient_email: "",
    tests: initialTests,
    package_ids: [],
    promo_code: "",
    collection_type: "home",
    preferred_date: "",
    preferred_time: "",
    branch_id: "",
    address: "",
    notes: "",
  });
  const [applied_promo, setAppliedPromo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/labs/by-slug/${slug}/booking-config`);
        if (!cancelled) setCfg(data);
      } catch {
        if (!cancelled) toast.error("Lab not found");
      } finally {
        if (!cancelled) setLoadingCfg(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Load 30-min collection slots whenever cfg is ready and the patient picks a date.
  useEffect(() => {
    if (!cfg?.lab_id || !form.preferred_date) { setSlots([]); return; }
    let cancelled = false;
    setSlotsLoading(true);
    api.get(`/labs/${cfg.lab_id}/booking-slots?date=${form.preferred_date}`)
      .then(({ data }) => { if (!cancelled) setSlots(data?.slots || []); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.lab_id, form.preferred_date]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTest = (name) => {
    if (!name.trim()) return;
    if (form.tests.includes(name.trim())) return;
    set("tests", [...form.tests, name.trim()]);
  };

  const removeTest = (name) => set("tests", form.tests.filter((t) => t !== name));

  const submit = async () => {
    if (!form.patient_name || form.patient_name.length < 2) return toast.error("Please enter your name");
    if (!form.patient_phone || form.patient_phone.replace(/\D/g, "").length < 7) return toast.error("Please enter a valid mobile number");
    if (form.tests.length === 0) return toast.error("Add at least one test");

    setSubmitting(true);
    try {
      // Whitelist + omit-empty so Pydantic never receives '' on optional EmailStr / date / time fields.
      const clean = (v) => (typeof v === "string" && !v.trim() ? undefined : v);
      const body = {
        lab_id: cfg.lab_id,
        report_id: params.get("reportId") || undefined,
        patient_name: form.patient_name.trim(),
        patient_phone: form.patient_phone.trim(),
        patient_email: clean(form.patient_email),
        tests: form.tests,
        collection_type: form.collection_type,
        preferred_date: clean(form.preferred_date),
        preferred_time: clean(form.preferred_time),
        branch_id: clean(form.branch_id),
        address: clean(form.address),
        notes: clean(form.notes),
        source: params.get("reportId") ? "smart_report" : "public_booking_page",
      };
      await api.post(`/labs/${cfg.lab_id}/booking-requests`, {
        ...body,
        package_ids: form.package_ids,
        promo_code: form.promo_code ? form.promo_code.toUpperCase() : undefined,
      });
      setSubmitted(true);
    } catch (e) {
      // Pydantic 422 returns `detail: [{ msg, loc, ... }]` — coerce to a plain string before toasting
      // or React will fatal with "Objects are not valid as a React child".
      const raw = e?.response?.data?.detail;
      let msg;
      if (Array.isArray(raw)) msg = raw.map((x) => x?.msg || JSON.stringify(x)).join(", ");
      else if (typeof raw === "string") msg = raw;
      else msg = "Could not submit booking. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingCfg) {
    return <div className="min-h-screen grid place-items-center text-ink/60" data-testid="pubbook-loading">Loading…</div>;
  }
  if (!cfg) {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <Card className="p-8 max-w-md text-center rounded-2xl">
          <div className="font-heading font-bold text-xl text-ink">Lab not found</div>
          <p className="text-sm text-ink/60 mt-2">The booking link you followed is invalid or has expired.</p>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen grid place-items-center px-6 bg-gradient-to-br from-white to-efgh-teal/5">
        <Card className="p-10 max-w-md text-center rounded-3xl" data-testid="pubbook-success">
          <CheckCircle2 className="w-14 h-14 text-efgh-green mx-auto" />
          <div className="mt-4 font-heading font-bold text-2xl text-ink">Booking request sent</div>
          <p className="mt-2 text-sm text-ink/60">
            {cfg.lab_name} has been notified and will contact you on <b>{form.patient_phone}</b> to confirm the collection slot.
          </p>
          <p className="mt-4 text-xs text-ink/40">You can close this page.</p>
        </Card>
        <div className="mt-6 flex items-center justify-center gap-1.5 text-[10px] text-ink/40" data-testid="pubbook-powered-by">
          <span className="uppercase tracking-[0.22em]">Powered by</span>
          <img src="/efgh-logo.png" alt="EFGH" className="h-5 opacity-70" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-efgh-teal/5 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6" data-testid="pubbook-lab-header">
          <div className="text-xs uppercase tracking-[0.22em] text-ink/50">Book with</div>
          <div className="mt-2 flex justify-center">
            {cfg.lab_logo_url ? (
              <img
                src={cfg.lab_logo_url}
                alt={cfg.lab_name || "Lab logo"}
                className="h-12 sm:h-14 w-auto object-contain"
                data-testid="pubbook-lab-logo"
              />
            ) : (
              <div className="font-heading font-bold text-3xl text-ink" data-testid="pubbook-lab-name">{cfg.lab_name}</div>
            )}
          </div>
        </div>

        {/* Promotional banners carousel (auto-rotates, swipeable) */}
        {(cfg.promo_banners || []).length > 0 && (
          <div className="mb-5" data-testid="pubbook-banners">
            <LabPromoBannerCarousel banners={cfg.promo_banners} labId={cfg.lab_id} />
          </div>
        )}

        {/* Packages from this lab — clicked packages append their tests to the tests list */}
        {(cfg.packages || []).length > 0 && (
          <Card className="p-5 rounded-2xl mb-5" data-testid="pubbook-packages">
            <div className="flex items-center gap-2 mb-3">
              <PackageIcon className="w-4 h-4 text-efgh-orange" />
              <div className="font-heading font-semibold text-ink">Health packages from {cfg.lab_name}</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {cfg.packages.slice(0, 6).map((p) => {
                const picked = form.package_ids.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`pubbook-package-${p.id}`}
                    onClick={() => setForm((f) => ({
                      ...f,
                      package_ids: picked
                        ? f.package_ids.filter((x) => x !== p.id)
                        : [...f.package_ids, p.id],
                    }))}
                    className={`text-left p-3 rounded-xl border transition-all ${picked ? "border-efgh-green bg-efgh-green/5 ring-2 ring-efgh-green/20" : "border-border hover:border-ink/20"}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-heading font-semibold text-ink text-sm truncate">{p.name}</div>
                        <div className="text-[11px] text-ink/50 line-clamp-1">{p.description || `${p.test_count || (p.tests || []).length} tests`}</div>
                      </div>
                      {picked && <Check className="w-4 h-4 text-efgh-green shrink-0" />}
                    </div>
                    <div className="mt-2 font-heading font-bold text-ink">₹{Number(p.price || 0).toLocaleString()}</div>
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        <Card className="p-6 rounded-2xl space-y-5" data-testid="pubbook-form">
          {/* Patient details */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Full name *</Label>
              <Input data-testid="pubbook-name" value={form.patient_name} onChange={(e) => set("patient_name", e.target.value)} placeholder="e.g., Priya Sharma" />
            </div>
            <div>
              <Label className="text-xs">Mobile *</Label>
              <Input data-testid="pubbook-phone" value={form.patient_phone} onChange={(e) => set("patient_phone", e.target.value)} placeholder="+91 …" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Email (optional)</Label>
              <Input data-testid="pubbook-email" value={form.patient_email} onChange={(e) => set("patient_email", e.target.value)} placeholder="you@example.com" />
            </div>
          </div>

          {/* Tests */}
          <div>
            <Label className="text-xs">Tests to book *</Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="pubbook-tests-list">
              {form.tests.map((t) => (
                <button
                  key={t}
                  onClick={() => removeTest(t)}
                  className="px-3 py-1.5 rounded-full text-xs bg-efgh-green/10 text-efgh-green border border-efgh-green/20 hover:bg-efgh-green/20"
                  title="Remove"
                >
                  {t} ×
                </button>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <Input
                placeholder="Add a test name…"
                data-testid="pubbook-add-test"
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addTest(e.currentTarget.value); e.currentTarget.value = ""; }
                }}
              />
            </div>
          </div>

          {/* Collection preference */}
          <div>
            <Label className="text-xs">Collection preference</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <button
                data-testid="pubbook-home"
                onClick={() => set("collection_type", "home")}
                className={`p-3 rounded-xl border flex items-center gap-2 text-sm ${form.collection_type === "home" ? "border-efgh-green bg-efgh-green/10 text-efgh-green" : "border-border text-ink/70"}`}
              >
                <Home className="w-4 h-4" /> Home Collection
              </button>
              <button
                data-testid="pubbook-lab"
                onClick={() => set("collection_type", "lab_visit")}
                className={`p-3 rounded-xl border flex items-center gap-2 text-sm ${form.collection_type === "lab_visit" ? "border-efgh-green bg-efgh-green/10 text-efgh-green" : "border-border text-ink/70"}`}
              >
                <Building2 className="w-4 h-4" /> Lab Visit
              </button>
            </div>
          </div>

          {form.collection_type === "lab_visit" && cfg.branches?.length > 0 && (
            <div>
              <Label className="text-xs">Preferred branch</Label>
              <select
                data-testid="pubbook-branch"
                value={form.branch_id}
                onChange={(e) => set("branch_id", e.target.value)}
                className="w-full mt-1 rounded-lg border border-border px-3 py-2 text-sm"
              >
                <option value="">Select branch</option>
                {cfg.branches.map((b) => (
                  <option key={b.id} value={b.id}>{`${b.name} — ${b.city}`}</option>
                ))}
              </select>
            </div>
          )}

          {form.collection_type === "home" && (
            <div>
              <Label className="text-xs">Collection address</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Flat / house, area, city, PIN" data-testid="pubbook-address" />
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <div>
              <Label className="text-xs h-4 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Preferred date</Label>
              <Input
                type="date"
                min={new Date().toISOString().slice(0, 10)}
                value={form.preferred_date}
                onChange={(e) => { set("preferred_date", e.target.value); set("preferred_time", ""); }}
                data-testid="pubbook-date"
                className="mt-1 h-10"
              />
            </div>
            <div>
              <Label className="text-xs h-4 flex items-center gap-1"><Clock className="w-3 h-3" /> Preferred time {slotsLoading ? "(loading…)" : ""}</Label>
              <select
                data-testid="pubbook-time"
                value={form.preferred_time}
                onChange={(e) => set("preferred_time", e.target.value)}
                disabled={!form.preferred_date || slotsLoading}
                className="w-full mt-1 rounded-md border border-input bg-transparent px-3 py-2 text-sm h-10 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="">{!form.preferred_date ? "Pick a date first" : slots.length ? "Select a time slot" : "Lab closed on this date"}</option>
                {slots.map((s) => (
                  <option key={s} value={s}>{formatSlot(s)}</option>
                ))}
              </select>
              {form.preferred_date && !slotsLoading && (
                <p className="text-[10px] text-ink/50 mt-1">
                  {slots.length ? `${slots.length} slots · matches lab's sample-collection hours` : "This lab does not operate on the selected day. Try another date."}
                </p>
              )}
            </div>
          </div>

          <div>
            <Label className="text-xs">Notes for the lab (optional)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="pubbook-notes" />
          </div>

          {/* Promo codes shown by the lab. Patient can click to auto-apply, or type manually. */}
          <div data-testid="pubbook-promo">
            <Label className="text-xs flex items-center gap-1"><Tag className="w-3 h-3 text-efgh-orange" /> Have a coupon?</Label>
            {(cfg.promo_codes || []).length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-2">
                {cfg.promo_codes.map((p) => {
                  const picked = form.promo_code.toUpperCase() === p.code.toUpperCase();
                  return (
                    <button
                      key={p.id || p.code}
                      type="button"
                      data-testid={`pubbook-promo-${p.code}`}
                      onClick={() => set("promo_code", picked ? "" : p.code)}
                      className={`text-left px-3 py-1.5 rounded-full text-xs border transition-all ${picked ? "border-efgh-orange bg-efgh-orange/10 text-efgh-orange font-semibold" : "border-dashed border-ink/30 text-ink/70 hover:border-efgh-orange/50"}`}
                      title={p.description || ""}
                    >
                      <span className="font-mono font-bold">{p.code}</span>
                      <span className="ml-2 text-[10px]">
                        {p.discount_type === "percent" ? `${p.value}% off` : `₹${p.value} off`}
                        {p.applies_to && p.applies_to !== "all" ? ` · ${p.applies_to}` : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <Input
              className="mt-2"
              placeholder="Or enter code (e.g. WELCOME10)"
              value={form.promo_code}
              onChange={(e) => set("promo_code", e.target.value.toUpperCase())}
              data-testid="pubbook-promo-input"
            />
            {form.promo_code && (
              <p className="text-[11px] text-efgh-orange mt-1">
                Code <b className="font-mono">{form.promo_code}</b> will be applied by the lab when they confirm your booking.
              </p>
            )}
          </div>

          <Button data-testid="pubbook-submit" onClick={submit} disabled={submitting} className="w-full bg-ink hover:bg-ink/90 text-white">
            {submitting ? "Sending…" : (<>Send booking request <ArrowRight className="w-4 h-4 ml-2" /></>)}
          </Button>
        </Card>

        <div className="mt-8 flex items-center justify-center gap-1.5 text-[10px] text-ink/40" data-testid="pubbook-powered-by">
          <span className="uppercase tracking-[0.22em]">Powered by</span>
          <img src="/efgh-logo.png" alt="EFGH" className="h-5 opacity-70" />
        </div>
      </div>
    </div>
  );
}
